// Tally sheet engine (field units). Pure functions so it works offline.
const CAP = 1029.4; // in² × ft -> bbl
const r2 = (n, d = 2) => (Number.isFinite(n) ? Math.round(n * 10 ** d) / 10 ** d + 0 : null);
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export const STRING_TYPES = {
  drill: { label: "Drill string", kinds: ["Bit", "BHA", "Drill collar", "HWDP", "Drill pipe", "Other"], bulkKind: "Drill pipe" },
  casing: { label: "Casing", kinds: ["Shoe", "Shoe track", "Float collar", "Casing", "Pup", "Other"], bulkKind: "Casing" },
  tubing: { label: "Tubing", kinds: ["Mule shoe", "Packer", "Nipple", "Tubing", "Pup", "Other"], bulkKind: "Tubing" },
};

let seq = 0;
export const newJoint = (j = {}) => ({ key: `j${Date.now()}${++seq}`, kind: "", desc: "", od: "", id: "", length: "", ...j });

// Joints are listed bottom-up (first row = bit / shoe). Returns rows with running length from the bottom.
export function runningTally(joints, offset = 0) {
  let run = 0;
  const rows = joints.map((j, i) => {
    run += num(j.length);
    return { ...j, n: i + 1, running: r2(run) };
  });
  return { rows, total: r2(run), bitDepth: r2(run + num(offset)) };
}

// String segments from surface down: [{top, bottom, od, id}]
function stringSegments(joints, bitDepth) {
  const segs = [];
  let bottom = bitDepth;
  for (const j of joints) {
    const len = num(j.length);
    if (len <= 0) continue;
    segs.push({ top: Math.max(bottom - len, 0), bottom, od: num(j.od), id: num(j.id) });
    bottom -= len;
  }
  return segs;
}

// Hole sections listed top-down by bottom depth: [{name, id, bottom}]
function holeIntervals(sections) {
  let top = 0;
  return sections
    .filter((s) => num(s.bottom) > 0 && num(s.id) > 0)
    .sort((a, b) => num(a.bottom) - num(b.bottom))
    .map((s) => {
      const iv = { name: s.name || "Section", id: num(s.id), top, bottom: num(s.bottom) };
      top = Math.max(top, iv.bottom);
      return iv;
    })
    .filter((iv) => iv.bottom > iv.top);
}

export function computeTally({ joints, sections, fluids, porePoints, offset }) {
  const { rows, total, bitDepth } = runningTally(joints, offset);
  const segs = stringSegments(joints, bitDepth);
  const tvd = num(fluids.tvd) || bitDepth;
  const pipeVol = segs.reduce((a, s) => a + (s.id ** 2 / CAP) * (s.bottom - s.top), 0);
  const steelDisp = segs.reduce((a, s) => a + ((s.od ** 2 - s.id ** 2) / CAP) * (s.bottom - s.top), 0);
  const closedDisp = segs.reduce((a, s) => a + (s.od ** 2 / CAP) * (s.bottom - s.top), 0);

  const holes = holeIntervals(sections).map((h) => {
    let vol = 0;
    for (const s of segs) {
      const ov = Math.min(h.bottom, s.bottom) - Math.max(h.top, s.top);
      if (ov > 0) vol += ((h.id ** 2 - s.od ** 2) / CAP) * ov;
    }
    const strLen = Math.max(0, Math.min(h.bottom, bitDepth) - h.top);
    const avgOd = strLen > 0 ? Math.sqrt(Math.max(h.id ** 2 - (vol * CAP) / strLen, 0)) : 0;
    return {
      name: h.name, id: h.id, top: r2(h.top), bottom: r2(h.bottom), length_with_string: r2(strLen),
      annular_cap_bbl_ft: strLen > 0 ? r2(vol / strLen, 5) : null, annular_vol_bbl: r2(vol), avg_string_od: r2(avgOd, 3),
    };
  });
  const annVol = holes.reduce((a, h) => a + (h.annular_vol_bbl || 0), 0);

  // Hydrostatics: annulus vs pipe side (with optional heavy slug at top of the pipe)
  const mwAnn = num(fluids.mw_annulus);
  const mwPipe = num(fluids.mw_pipe) || mwAnn;
  const slugWt = num(fluids.slug_weight);
  const topCap = segs[segs.length - 1] ? segs[segs.length - 1].id ** 2 / CAP : 0;
  const slugLen = slugWt > 0 && topCap > 0 ? Math.min(num(fluids.slug_volume) / topCap, tvd) : 0;
  const pAnn = 0.052 * mwAnn * tvd;
  const pPipe = 0.052 * (slugWt * slugLen + mwPipe * (tvd - slugLen));
  const diff = pPipe - pAnn;
  const pump = num(fluids.pump_output);

  const overbalance = porePoints
    .filter((p) => num(p.depth) > 0 && (num(p.pore_ppg) > 0 || num(p.pore_psi) > 0))
    .sort((a, b) => num(a.depth) - num(b.depth))
    .map((p) => {
      const d = num(p.depth);
      const pore = num(p.pore_psi) || 0.052 * num(p.pore_ppg) * d;
      const hyd = 0.052 * mwAnn * d;
      return { depth: d, label: p.label || "", hydrostatic_psi: r2(hyd, 0), pore_psi: r2(pore, 0), overbalance_psi: r2(hyd - pore, 0), emw_margin_ppg: r2((hyd - pore) / (0.052 * d), 2) };
    });

  return {
    rows,
    summary: {
      joints: rows.length, string_length_ft: total, bit_depth_ft: bitDepth, tvd_ft: r2(tvd),
      pipe_volume_bbl: r2(pipeVol), annular_volume_bbl: r2(annVol), total_hole_volume_bbl: r2(pipeVol + annVol),
      steel_displacement_bbl: r2(steelDisp), closed_end_displacement_bbl: r2(closedDisp),
      strokes_surface_to_bit: pump > 0 ? Math.round(pipeVol / pump) : null,
      strokes_bottoms_up: pump > 0 ? Math.round(annVol / pump) : null,
      hydrostatic_annulus_psi: r2(pAnn, 0), hydrostatic_pipe_psi: r2(pPipe, 0), differential_psi: r2(diff, 0),
      slug_length_ft: r2(slugLen, 0),
    },
    holes,
    overbalance,
  };
}

// Parse imported rows (array of arrays from CSV/Excel) into joints.
export function rowsToJoints(rows, defaults) {
  const clean = rows.filter((r) => r && r.some((c) => String(c ?? "").trim() !== ""));
  if (!clean.length) return [];
  const head = clean[0].map((c) => String(c ?? "").toLowerCase());
  const hasHeader = head.some((h) => /len|desc|od|id|type|kind/.test(h)) && !head.some((h) => /^\d+(\.\d+)?$/.test(h));
  const col = (re) => (hasHeader ? head.findIndex((h) => re.test(h)) : -1);
  let iLen = col(/len/);
  const iDesc = col(/desc|item|comp/), iOd = col(/\bod\b|o\.d/), iId = col(/\bid\b|i\.d/), iKind = col(/type|kind/);
  const body = hasHeader ? clean.slice(1) : clean;
  if (iLen < 0 && !hasHeader) iLen = (body[0] || []).findIndex((c) => Number.isFinite(parseFloat(c)) && parseFloat(c) > 1);
  if (iLen < 0) return [];
  return body
    .map((r) => ({ r, len: parseFloat(r[iLen]) }))
    .filter(({ len }) => Number.isFinite(len) && len > 0)
    .map(({ r, len }) =>
      newJoint({
        kind: (iKind >= 0 && r[iKind]) || defaults.kind,
        desc: iDesc >= 0 ? String(r[iDesc] ?? "") : "",
        od: (iOd >= 0 && r[iOd] !== "" && r[iOd] != null ? String(r[iOd]) : defaults.od),
        id: (iId >= 0 && r[iId] !== "" && r[iId] != null ? String(r[iId]) : defaults.id),
        length: String(len),
      })
    );
}

export async function parseTallyFile(file) {
  const XLSX = await import("xlsx");
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });
}
